import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { GooglePlacesAutocomplete } from './ui/google-places-autocomplete';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, GripVertical, Trash2, MapPin, Package, Flag, Navigation, ChevronDown, Building2, User, RotateCcw } from 'lucide-react';

const STOP_TYPE_CONFIG = {
  pickup: { label: 'Pickup', icon: Package, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  dropoff: { label: 'Dropoff', icon: Flag, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  waypoint: { label: 'Waypoint', icon: Navigation, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
};

const emptyStop = () => ({
  id: crypto.randomUUID(),
  order: 0,
  type: 'pickup',
  label: '',
  address: '',
  suburb: '',
  state: '',
  postcode: '',
  notes: '',
  est_arrival: '',
  status: 'pending',
  site_id: null,
  site_contact_id: null,
  site_contact_name: null,
});

function formatSiteAddr(site) {
  if (!site) return '';
  if (site.street_address || site.suburb) {
    return [site.street_address, site.suburb, site.state, site.postcode].filter(Boolean).join(', ');
  }
  return site.address || '';
}

function SiteSelector({ sites, value, onSelect, onAddNew, disabled }) {
  const [open, setOpen] = useState(false);
  const selected = value ? sites.find(s => s.id === value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal h-8 text-xs" disabled={disabled} data-testid="stop-site-select">
          {selected ? (
            <span className="truncate flex items-center gap-1.5">
              <Building2 className="h-3 w-3 text-[#ffd108] shrink-0" />
              {selected.name ? `${selected.name} - ${formatSiteAddr(selected)}` : formatSiteAddr(selected) || 'Unnamed Site'}
            </span>
          ) : (
            <span className="text-muted-foreground">Select existing site or enter manually below</span>
          )}
          <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search sites..." className="text-xs" />
          <CommandList className="max-h-[250px] overflow-y-auto">
            <CommandEmpty>No sites found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__add_new_site__"
                onSelect={() => { onAddNew(); setOpen(false); }}
                className="text-[#ffd108] font-medium text-xs"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> ADD NEW SITE
              </CommandItem>
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onSelect(null); setOpen(false); }}
                  className="text-muted-foreground text-xs"
                >
                  <Trash2 className="mr-1.5 h-3 w-3" /> Clear selection (manual entry)
                </CommandItem>
              )}
            </CommandGroup>
            {sites.length > 0 && (
              <CommandGroup heading="Sites">
                {sites.map(s => (
                  <CommandItem
                    key={s.id}
                    value={`${s.name || ''} ${formatSiteAddr(s)}`}
                    onSelect={() => { onSelect(s); setOpen(false); }}
                    className="text-xs"
                  >
                    <MapPin className="mr-1.5 h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="truncate">{s.name ? `${s.name} - ` : ''}{formatSiteAddr(s) || 'Unnamed'}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SiteContactSelector({ api, siteId, value, valueName, onChange, idx }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', job_title: '', phone: '', email: '' });

  const load = useCallback(async () => {
    if (!siteId || !api) { setContacts([]); return; }
    try {
      const res = await api.get(`/sites/${siteId}/contacts`);
      setContacts(res.data || []);
    } catch (e) {
      setContacts([]);
    }
  }, [siteId, api]);

  useEffect(() => { load(); }, [load]);

  const selected = value ? contacts.find(c => c.id === value) : null;
  const displayName = selected?.name || valueName;

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Contact name is required'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/sites/${siteId}/contacts`, {
        site_id: siteId,
        name: form.name.trim(),
        job_title: form.job_title.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      });
      const nc = res.data;
      setContacts(prev => [...prev, nc]);
      onChange(nc.id, nc.name);
      toast.success('Site contact created');
      setCreateOpen(false);
      setForm({ name: '', job_title: '', phone: '', email: '' });
    } catch (e) {
      toast.error('Failed to create site contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className="w-full justify-between font-normal h-8 text-xs" data-testid={`stop-${idx}-site-contact-select`}>
            {displayName ? (
              <span className="truncate flex items-center gap-1.5">
                <User className="h-3 w-3 text-[#ffd108] shrink-0" />
                {displayName}
              </span>
            ) : (
              <span className="text-muted-foreground flex items-center gap-1.5">
                <User className="h-3 w-3 shrink-0" /> Select site contact (optional)
              </span>
            )}
            <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search contacts..." className="text-xs" />
            <CommandList className="max-h-[250px] overflow-y-auto">
              <CommandEmpty>No contacts found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__add_new_site_contact__"
                  onSelect={() => { setOpen(false); setCreateOpen(true); }}
                  className="text-[#ffd108] font-medium text-xs"
                  data-testid={`stop-${idx}-add-site-contact`}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> ADD NEW CONTACT
                </CommandItem>
                {value && (
                  <CommandItem
                    value="__clear_contact__"
                    onSelect={() => { onChange(null, null); setOpen(false); }}
                    className="text-muted-foreground text-xs"
                  >
                    <Trash2 className="mr-1.5 h-3 w-3" /> Clear selection
                  </CommandItem>
                )}
              </CommandGroup>
              {contacts.length > 0 && (
                <CommandGroup heading="Site Contacts">
                  {contacts.map(c => (
                    <CommandItem
                      key={c.id}
                      value={c.name || `site-contact-${c.id}`}
                      onSelect={() => { onChange(c.id, c.name); setOpen(false); }}
                      className="text-xs"
                    >
                      <User className="mr-1.5 h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="truncate flex items-center gap-1.5">
                        {c.name || 'Unnamed'}
                        {c.job_title && <span className="text-muted-foreground">({c.job_title})</span>}
                        {c.is_primary && <Badge variant="outline" className="text-[10px] ml-1">Primary</Badge>}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`stop-${idx}-create-site-contact-dialog`}>
          <DialogHeader>
            <DialogTitle>New Site Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                data-testid={`stop-${idx}-site-contact-name`}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Job Title</Label>
              <Input
                value={form.job_title}
                onChange={(e) => setForm(prev => ({ ...prev, job_title: e.target.value }))}
                placeholder="e.g. Site Manager"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone"
                  data-testid={`stop-${idx}-site-contact-phone`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  data-testid={`stop-${idx}-site-contact-email`}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid={`stop-${idx}-save-site-contact`}>
              {saving ? 'Saving...' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function HaulageStopsEditor({ stops = [], onChange, country = 'AU', sites = [], onAddNewSite, api = null }) {
  const [dragIdx, setDragIdx] = useState(null);

  const addStop = (type = 'dropoff') => {
    const newStop = emptyStop();
    newStop.type = type;
    newStop.order = stops.length + 1;
    onChange([...stops, newStop]);
  };

  const removeStop = (idx) => {
    const updated = stops.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
    onChange(updated);
  };

  const restoreStop = (idx) => {
    const updated = [...stops];
    const { deleted, deleted_at, deleted_by, deleted_by_name, deleted_reason, deleted_reason_code, deleted_reason_note, ...rest } = updated[idx];
    updated[idx] = { ...rest, deleted: false };
    onChange(updated);
  };

  const updateStop = (idx, field, value) => {
    const updated = [...stops];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const handleSiteSelect = (idx, site) => {
    const updated = [...stops];
    if (!site) {
      // Clear — user wants manual entry
      updated[idx] = { ...updated[idx], site_id: null, site_contact_id: null, site_contact_name: null };
    } else {
      updated[idx] = {
        ...updated[idx],
        site_id: site.id,
        // Reset the per-stop contact when the site changes
        site_contact_id: updated[idx].site_id === site.id ? updated[idx].site_contact_id : null,
        site_contact_name: updated[idx].site_id === site.id ? updated[idx].site_contact_name : null,
        address: site.street_address || site.address || '',
        suburb: site.suburb || '',
        state: site.state || '',
        postcode: site.postcode || '',
      };
    }
    onChange(updated);
  };

  const setStopContact = (idx, contactId, contactName) => {
    const updated = [...stops];
    updated[idx] = { ...updated[idx], site_contact_id: contactId, site_contact_name: contactName };
    onChange(updated);
  };

  const handlePlaceSelect = (idx, place) => {
    const updated = [...stops];
    updated[idx] = {
      ...updated[idx],
      site_id: null, // Manual entry clears site link
      site_contact_id: null,
      site_contact_name: null,
      address: place.street_address || '',
      suburb: place.suburb || '',
      state: place.state || '',
      postcode: place.postcode || '',
    };
    onChange(updated);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const updated = [...stops];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    const reordered = updated.map((s, i) => ({ ...s, order: i + 1 }));
    onChange(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  return (
    <div className="space-y-3" data-testid="haulage-stops-editor">
      {stops.length === 0 && (
        <div className="text-center py-6 border-2 border-dashed rounded-lg border-border text-muted-foreground">
          <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No stops added yet</p>
          <p className="text-xs opacity-70">Add pickup and dropoff locations for this route</p>
        </div>
      )}

      {stops.map((stop, idx) => {
        const config = STOP_TYPE_CONFIG[stop.type] || STOP_TYPE_CONFIG.dropoff;
        const Icon = config.icon;
        if (stop.deleted) {
          const addr = [stop.address, stop.suburb, stop.state, stop.postcode].filter(Boolean).join(', ');
          return (
            <div
              key={stop.id}
              className="border rounded-lg p-3 border-red-500/40 bg-red-500/5"
              data-testid={`stop-${idx}-deleted`}
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${config.color} line-through opacity-70`}>
                  <Icon className="h-3 w-3 mr-1" />
                  {config.label}
                </Badge>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600">Deleted</span>
                <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => restoreStop(idx)}
                  data-testid={`stop-${idx}-restore`}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                </Button>
              </div>
              <p className="text-sm line-through text-muted-foreground mt-1 truncate">{stop.label || addr || `Stop ${idx + 1}`}</p>
              <p className="text-xs text-red-600 mt-0.5" data-testid={`stop-${idx}-deleted-reason`}>
                Deleted by {stop.deleted_by_name || 'Driver'}{stop.deleted_reason ? ` — ${stop.deleted_reason}` : ''}
              </p>
            </div>
          );
        }
        return (
          <div
            key={stop.id}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            className={`border rounded-lg p-3 space-y-2 transition-all border-border bg-muted/50 ${dragIdx === idx ? 'opacity-50 scale-95' : ''}`}
            data-testid={`stop-${idx}`}
          >
            {/* Stop header */}
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />
              <Badge variant="outline" className={`text-xs ${config.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {config.label}
              </Badge>
              <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
              <div className="flex-1" />
              <Select value={stop.type} onValueChange={(v) => updateStop(idx, 'type', v)}>
                <SelectTrigger className="w-28 h-7 text-xs" data-testid={`stop-${idx}-type`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="dropoff">Dropoff</SelectItem>
                  <SelectItem value="waypoint">Waypoint</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={() => removeStop(idx)}
                data-testid={`stop-${idx}-remove`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Location name / title — usable instead of a full address */}
            <Input
              value={stop.label || ''}
              onChange={(e) => updateStop(idx, 'label', e.target.value)}
              placeholder="Location name / title (e.g. North Depot) — optional"
              className="text-xs h-8"
              data-testid={`stop-${idx}-label`}
            />

            {/* Site selector — always visible so a site can be picked or added */}
            <SiteSelector
              sites={sites}
              value={stop.site_id}
              onSelect={(site) => handleSiteSelect(idx, site)}
              onAddNew={() => onAddNewSite && onAddNewSite(idx)}
            />

            {/* Site Contact selector — only when a saved site is linked to this stop */}
            {stop.site_id && api && (
              <SiteContactSelector
                api={api}
                idx={idx}
                siteId={stop.site_id}
                value={stop.site_contact_id}
                valueName={stop.site_contact_name}
                onChange={(contactId, contactName) => setStopContact(idx, contactId, contactName)}
              />
            )}

            {/* Address search — always visible for manual override */}
            {!stop.site_id && (
              <GooglePlacesAutocomplete
                placeholder="Search address..."
                country={country}
                value={stop.address || ''}
                onChange={(e) => updateStop(idx, 'address', e.target.value)}
                onPlaceSelect={(place) => handlePlaceSelect(idx, place)}
                className="text-sm"
                data-testid={`stop-${idx}-address-search`}
              />
            )}

            {/* Address fields row */}
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={stop.suburb || ''}
                onChange={(e) => { updateStop(idx, 'suburb', e.target.value); updateStop(idx, 'site_id', null); }}
                placeholder="Suburb"
                className="text-xs h-8"
                data-testid={`stop-${idx}-suburb`}
                readOnly={!!stop.site_id}
              />
              <Input
                value={stop.state || ''}
                onChange={(e) => { updateStop(idx, 'state', e.target.value); updateStop(idx, 'site_id', null); }}
                placeholder="State"
                className="text-xs h-8"
                readOnly={!!stop.site_id}
              />
              <Input
                value={stop.postcode || ''}
                onChange={(e) => { updateStop(idx, 'postcode', e.target.value); updateStop(idx, 'site_id', null); }}
                placeholder="Postcode"
                className="text-xs h-8"
                readOnly={!!stop.site_id}
              />
            </div>

            {/* Notes & ETA */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input
                  value={stop.notes || ''}
                  onChange={(e) => updateStop(idx, 'notes', e.target.value)}
                  placeholder="Notes (e.g. Load 2x generators)"
                  className="text-xs h-8"
                  data-testid={`stop-${idx}-notes`}
                />
              </div>
              <div className="flex gap-1 items-center">
                <Select
                  value={stop.est_arrival?.split(':')[0] || ''}
                  onValueChange={(hour) => {
                    const currentMinute = stop.est_arrival?.split(':')[1] || '00';
                    updateStop(idx, 'est_arrival', `${hour}:${currentMinute}`);
                  }}
                >
                  <SelectTrigger className="w-16 h-8 text-xs font-mono" data-testid={`stop-${idx}-eta-hour`}>
                    <SelectValue placeholder="HH" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs font-bold text-muted-foreground">:</span>
                <Select
                  value={stop.est_arrival?.split(':')[1] || ''}
                  onValueChange={(minute) => {
                    const currentHour = stop.est_arrival?.split(':')[0] || '06';
                    updateStop(idx, 'est_arrival', `${currentHour}:${minute}`);
                  }}
                >
                  <SelectTrigger className="w-16 h-8 text-xs font-mono" data-testid={`stop-${idx}-eta-min`}>
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent>
                    {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      })}

      {/* Add stop buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addStop('pickup')}
          className="flex-1"
          data-testid="add-pickup-stop"
        >
          <Package className="h-3.5 w-3.5 mr-1" /> Pickup
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addStop('dropoff')}
          className="flex-1"
          data-testid="add-dropoff-stop"
        >
          <Flag className="h-3.5 w-3.5 mr-1" /> Dropoff
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addStop('waypoint')}
          className="flex-1"
          data-testid="add-waypoint-stop"
        >
          <Navigation className="h-3.5 w-3.5 mr-1" /> Waypoint
        </Button>
      </div>
    </div>
  );
}
